import React, { useState } from 'react';
import { Check, Copy, Loader2, X, KeyRound, Webhook, ShieldCheck, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Step = ({ n, children }) => (
  <li className="flex items-start gap-2.5">
    <span className="w-5 h-5 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-[11px] font-bold flex-shrink-0 mt-0.5">{n}</span>
    <span className="text-sm text-zinc-600">{children}</span>
  </li>
);

const GoCardlessGuidedSetup = ({ open, onClose, onComplete, startStep = 0 }) => {
  const [step, setStep] = useState(startStep);
  const [token, setToken] = useState('');
  const [environment, setEnvironment] = useState('live');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [accountName, setAccountName] = useState('');
  const [secret, setSecret] = useState('');
  const [copied, setCopied] = useState(false);

  const webhookUrl = `${API_URL}/api/webhook/gocardless`;
  const authHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('streakfree_token')}`,
    'Content-Type': 'application/json'
  });

  if (!open) return null;

  const connectToken = async () => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/integrations/gocardless/connect-api-key`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ access_token: token.trim(), environment })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Connection failed — check the token and try again');
      setAccountName(data.account_name || 'GoCardless Account');
      setStep(1);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const saveSecret = async () => {
    setBusy(true); setError(null);
    try {
      const res = await fetch(`${API_URL}/api/integrations/gocardless/webhook-secret`, {
        method: 'PUT', headers: authHeaders(),
        body: JSON.stringify({ webhook_secret: secret.trim() })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to save the secret');
      setStep(3);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(webhookUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const titles = [
    { icon: KeyRound, title: 'Get your access token', sub: 'Step 1 of 3 — takes 2 minutes' },
    { icon: Webhook, title: 'Add the webhook', sub: 'Step 2 of 3 — copy one link across' },
    { icon: ShieldCheck, title: 'Paste the secret', sub: 'Step 3 of 3 — last bit' },
    { icon: Check, title: "You're connected!", sub: 'Direct debit is ready' },
  ];
  const T = titles[step]; const Icon = T.icon;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center sm:p-4" data-testid="gc-guided-setup">
      <div className="w-full max-w-md bg-white sm:rounded-2xl rounded-t-3xl shadow-xl overflow-hidden max-h-[92vh] flex flex-col">
        <div className="p-5 border-b border-zinc-100 flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-teal-100 flex items-center justify-center flex-shrink-0">
            <Icon size={22} className="text-teal-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-zinc-900">{T.title}</h2>
            <p className="text-xs text-zinc-500">{T.sub}</p>
          </div>
          <div className="flex gap-1 mr-1">
            {[0,1,2].map(i => (
              <div key={i} className={`h-1.5 rounded-full transition-all ${i === step ? 'w-5 bg-teal-500' : i < step ? 'w-1.5 bg-teal-300' : 'w-1.5 bg-zinc-200'}`} />
            ))}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-zinc-100 rounded-lg" data-testid="gc-guided-close">
            <X size={18} className="text-zinc-400" />
          </button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm" data-testid="gc-guided-error">{error}</div>
          )}

          {step === 0 && (
            <>
              <ol className="space-y-2.5">
                <Step n={1}>Log in to <a href="https://manage.gocardless.com" target="_blank" rel="noreferrer" className="text-teal-600 font-semibold inline-flex items-center gap-0.5">GoCardless <ExternalLink size={11} /></a></Step>
                <Step n={2}>Click your <strong>company name</strong> (bottom left) → <strong>Developers</strong></Step>
                <Step n={3}>Click <strong>Create</strong> → <strong>Access token</strong></Step>
                <Step n={4}>Name it <strong>StreakFree</strong>, choose <strong>Read-write access</strong>, click Create</Step>
                <Step n={5}>Copy the token — it's only shown once — and paste it below</Step>
              </ol>
              <div>
                <label className="text-xs font-semibold text-zinc-600">Access token</label>
                <input
                  type="password" value={token} onChange={e => setToken(e.target.value)}
                  placeholder="live_..."
                  className="w-full mt-1 px-3 py-2.5 rounded-xl border border-zinc-200 text-sm font-mono focus:outline-none focus:border-teal-500"
                  data-testid="gc-guided-token-input"
                />
              </div>
              <div className="flex gap-2">
                {['live','sandbox'].map(env => (
                  <button key={env} onClick={() => setEnvironment(env)}
                    className={`flex-1 py-2 rounded-xl border-2 text-sm font-semibold capitalize transition-all ${environment === env ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-zinc-200 text-zinc-500'}`}
                    data-testid={`gc-guided-env-${env}`}
                  >{env}</button>
                ))}
              </div>
              <button onClick={connectToken} disabled={busy || !token.trim()}
                className="w-full py-3 rounded-xl bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white text-sm font-bold flex items-center justify-center gap-2"
                data-testid="gc-guided-connect-btn"
              >
                {busy ? <><Loader2 size={16} className="animate-spin" /> Checking token…</> : 'Connect'}
              </button>
            </>
          )}

          {step === 1 && (
            <>
              {accountName && (
                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-xl text-sm text-emerald-800 flex items-center gap-2" data-testid="gc-guided-connected-badge">
                  <Check size={16} /> Connected to <strong>{accountName}</strong>
                </div>
              )}
              <p className="text-sm text-zinc-600">The webhook tells StreakFree instantly when payments succeed or fail. Copy this link:</p>
              <div className="flex gap-2">
                <input type="text" value={webhookUrl} readOnly
                  className="flex-1 px-3 py-2.5 bg-zinc-50 border border-zinc-200 rounded-xl text-xs font-mono text-zinc-600 truncate"
                  data-testid="gc-guided-webhook-url"
                />
                <button onClick={copyUrl}
                  className={`px-3.5 py-2.5 rounded-xl text-xs font-bold flex items-center gap-1.5 ${copied ? 'bg-emerald-500 text-white' : 'bg-zinc-900 text-white'}`}
                  data-testid="gc-guided-webhook-copy"
                >
                  <Copy size={13} /> {copied ? 'Copied!' : 'Copy'}
                </button>
              </div>
              <ol className="space-y-2.5">
                <Step n={1}>In GoCardless, go to <strong>Developers</strong> → <strong>Create</strong> → <strong>Webhook endpoint</strong></Step>
                <Step n={2}>Name: <strong>StreakFree</strong> — URL: <strong>paste the link you just copied</strong></Step>
                <Step n={3}>Copy the <strong>Secret</strong> shown on the form, then click <strong>Create webhook endpoint</strong></Step>
              </ol>
              <button onClick={() => setStep(2)}
                className="w-full py-3 rounded-xl bg-teal-500 hover:bg-teal-600 text-white text-sm font-bold"
                data-testid="gc-guided-webhook-done-btn"
              >
                Done — I've created the webhook
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <p className="text-sm text-zinc-600">Paste the <strong>Secret</strong> from the webhook you just created. StreakFree uses it to make sure payment updates really come from GoCardless.</p>
              <input
                type="password" value={secret} onChange={e => setSecret(e.target.value)}
                placeholder="Paste your webhook secret"
                className="w-full px-3 py-2.5 rounded-xl border border-zinc-200 text-sm font-mono focus:outline-none focus:border-teal-500"
                data-testid="gc-guided-secret-input"
              />
              <button onClick={saveSecret} disabled={busy || !secret.trim()}
                className="w-full py-3 rounded-xl bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white text-sm font-bold flex items-center justify-center gap-2"
                data-testid="gc-guided-secret-save-btn"
              >
                {busy ? <><Loader2 size={16} className="animate-spin" /> Saving…</> : 'Save & verify'}
              </button>
              <button onClick={() => setStep(1)} className="w-full py-2 text-xs font-semibold text-zinc-400" data-testid="gc-guided-back-btn">
                ← Back to webhook instructions
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <div className="space-y-2" data-testid="gc-guided-success">
                {[`Token connected${accountName ? ` — ${accountName}` : ''}`, 'Webhook secret saved & verified', 'Direct debit payments will now sync automatically'].map((t, i) => (
                  <div key={i} className="flex items-center gap-2.5 p-3 bg-emerald-50 rounded-xl text-sm text-emerald-800">
                    <Check size={16} className="flex-shrink-0" /> {t}
                  </div>
                ))}
              </div>
              <button onClick={() => { onComplete && onComplete(); onClose(); }}
                className="w-full py-3 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold"
                data-testid="gc-guided-finish-btn"
              >
                Finish
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoCardlessGuidedSetup;
